import { createPinia } from 'pinia'
import { createApp } from 'vue'
import App from './App.vue'
import api from './api/axios';
import router from './router'
import errorLogger from './utils/vueErrorLogger.js'

import './main.css'

import LandingPage from './components/LandingPage.vue'
import BaseModal from './components/BaseModal.vue'
import BaseMessages from './components/BaseMessages.vue'
import NavigationBar from './components/NavigationBar.vue'
import SidebarNavigation from './components/SidebarNavigation.vue';
import DotsWaveLoader from './components/DotsWaveLoader.vue';

const app = createApp(App)

app.config.globalProperties.$http = api;

// Track if a navigation is in progress (router.onError will handle those chunk errors)
let isNavigating = false;
router.beforeEach(() => { isNavigating = true; });
router.afterEach(() => { isNavigating = false; });
router.onError(() => { isNavigating = false; });

// Global error handler for chunk loading failures outside of navigation
app.config.errorHandler = (err, instance, info) => {
    console.error('Global error:', err, info);

    const isChunkError = err.message && (
        /Loading chunk \d+ failed/.test(err.message) ||
        /Failed to fetch dynamically imported module/.test(err.message) ||
        err.message.includes('ChunkLoadError')
    );

    if (isChunkError) {
        // If navigating, let router.onError handle it to avoid duplicate reloads
        if (isNavigating) {
            return;
        }

        // Non-navigation chunk error (e.g., user returned to stale tab)
        errorLogger.logError({
            type: 'component_chunk_load_error',
            message: err.message,
            stack: err.stack,
            url: window.location.href,
            userAgent: navigator.userAgent,
            info: info
        });

        const lastReload = sessionStorage.getItem('last-chunk-reload');
        const now = Date.now();

        if (!lastReload || (now - parseInt(lastReload)) > 30000) {
            sessionStorage.setItem('last-chunk-reload', now.toString());
            window.location.reload();
        } else {
            errorLogger.logError({
                type: 'component_chunk_load_error_reload_prevented',
                message: 'Chunk load failed multiple times, preventing reload loop',
                originalError: err.message,
                url: window.location.href,
                userAgent: navigator.userAgent
            });
        }
    } else {
        // Log non-chunk errors
        errorLogger.logError({
            type: 'vue_error',
            message: err.message,
            stack: err.stack,
            url: window.location.href,
            userAgent: navigator.userAgent,
            info: info
        });
    }
};

app
    .use(createPinia())
    .use(router)

    .component('LandingPage', LandingPage)
    .component('BaseModal', BaseModal)
    .component('BaseMessages', BaseMessages)
    .component('NavigationBar', NavigationBar)
    .component('SidebarNavigation', SidebarNavigation)
    .component('DotsWaveLoader', DotsWaveLoader)

    .mount('#app');

app.directive('smart-tooltip', {
    mounted(el) {
        const tooltip = el.querySelector('.tooltip-text');
        if (!tooltip) return;

        // Store handlers on the element so they can be removed later
        const clickHandler = (event) => {
            event.preventDefault();
            event.stopPropagation();
        };

        const hideTooltip = () => {
            tooltip.style.visibility = 'hidden';
            tooltip.style.opacity = '0';
        };

        const scrollHandler = () => {
            hideTooltip();
        };

        const mouseenterHandler = () => {
            // Reset any JS-applied hide so CSS :hover can take over
            tooltip.style.visibility = '';
            tooltip.style.opacity = '';

            const rect = el.getBoundingClientRect();
            const modal = el.closest('.container.modal, .container.drawer');
            const isSmall = tooltip.classList.contains('tooltip-text-sm');
            const tooltipWidth = isSmall ? 100 : 280;

            if (modal) {
                const wrapperRect = el.getBoundingClientRect();
                const modalRect = modal.getBoundingClientRect();
                const isMobile = window.innerWidth <= 768;

                if (isMobile) {
                    // Mobile: use fixed positioning to escape overflow:auto scrollbar issue
                    // Position below the icon, right-aligned to the icon so it stays close
                    const modalPadding = 16;
                    const maxTooltipWidth = modalRect.width - (modalPadding * 2);
                    const effectiveWidth = Math.min(tooltipWidth, maxTooltipWidth);

                    // Right edge of tooltip aligns with right edge of icon
                    let tooltipLeft = wrapperRect.right - effectiveWidth;
                    // Clamp so it doesn't go outside the modal
                    tooltipLeft = Math.max(modalRect.left + modalPadding, tooltipLeft);
                    tooltipLeft = Math.min(tooltipLeft, modalRect.right - modalPadding - effectiveWidth);

                    tooltip.style.position = 'fixed';
                    tooltip.style.zIndex = '10000';
                    tooltip.style.margin = '0';
                    tooltip.style.right = 'auto';
                    tooltip.style.left = tooltipLeft + 'px';
                    tooltip.style.top = (wrapperRect.bottom + 8) + 'px';
                    tooltip.style.maxWidth = maxTooltipWidth + 'px';
                } else {
                    // Desktop: use absolute positioning (works fine, no scrollbar issues)
                    tooltip.style.position = 'absolute';
                    tooltip.style.zIndex = '10000';
                    tooltip.style.top = '-10px';

                    // Calculate space within modal bounds
                    const spaceToRight = modalRect.right - wrapperRect.right - 8;
                    const spaceToLeft = wrapperRect.left - modalRect.left - 8;

                    if (spaceToRight >= tooltipWidth) {
                        tooltip.style.left = '24px';
                        tooltip.style.right = 'auto';
                    } else if (spaceToLeft >= tooltipWidth) {
                        tooltip.style.right = '24px';
                        tooltip.style.left = 'auto';
                    } else {
                        // Not enough space on either side - position below the icon
                        tooltip.style.left = 'auto';
                        tooltip.style.right = 'auto';
                        const modalContentWidth = modalRect.width;
                        const wrapperCenterInModal = wrapperRect.left - modalRect.left + (wrapperRect.width / 2);
                        const tooltipLeft = Math.max(
                            8,
                            Math.min(
                                wrapperCenterInModal - (tooltipWidth / 2),
                                modalContentWidth - tooltipWidth - 8
                            )
                        );
                        tooltip.style.left = tooltipLeft + 'px';
                        tooltip.style.transform = 'none';
                    }
                }

                // Listen for scroll on the modal's content container to hide tooltip
                const scrollContainer = modal.querySelector('.content');
                if (scrollContainer) {
                    scrollContainer.addEventListener('scroll', scrollHandler, { once: true, passive: true });
                    el._tooltipScrollContainer = scrollContainer;
                }
            } else {
                // Outside modal: use fixed positioning
                const idealLeft = rect.right + 8;
                const availableSpace = window.innerWidth - idealLeft;

                tooltip.style.position = 'fixed';
                tooltip.style.margin = '0';
                tooltip.style.left = availableSpace >= tooltipWidth
                    ? idealLeft + 'px'
                    : (window.innerWidth - tooltipWidth - 16) + 'px';
                tooltip.style.top = rect.top + 'px';
                tooltip.style.zIndex = '10000';
            }
        };

        const mouseleaveHandler = () => {
            // Clean up scroll listener when mouse leaves
            if (el._tooltipScrollContainer) {
                el._tooltipScrollContainer.removeEventListener('scroll', scrollHandler);
                delete el._tooltipScrollContainer;
            }
            // Reset inline styles so CSS handles the hide transition
            tooltip.style.visibility = '';
            tooltip.style.opacity = '';
        };

        // Store handlers on element for cleanup
        el._tooltipClickHandler = clickHandler;
        el._tooltipMouseenterHandler = mouseenterHandler;
        el._tooltipMouseleaveHandler = mouseleaveHandler;
        el._tooltipScrollHandler = scrollHandler;

        // Add event listeners
        el.addEventListener('click', clickHandler);
        el.addEventListener('mouseenter', mouseenterHandler);
        el.addEventListener('mouseleave', mouseleaveHandler);
    },

    unmounted(el) {
        // Clean up event listeners
        if (el._tooltipClickHandler) {
            el.removeEventListener('click', el._tooltipClickHandler);
            delete el._tooltipClickHandler;
        }
        if (el._tooltipMouseenterHandler) {
            el.removeEventListener('mouseenter', el._tooltipMouseenterHandler);
            delete el._tooltipMouseenterHandler;
        }
        if (el._tooltipMouseleaveHandler) {
            el.removeEventListener('mouseleave', el._tooltipMouseleaveHandler);
            delete el._tooltipMouseleaveHandler;
        }
        if (el._tooltipScrollContainer) {
            el._tooltipScrollContainer.removeEventListener('scroll', el._tooltipScrollHandler);
            delete el._tooltipScrollContainer;
            delete el._tooltipScrollHandler;
        }
    }
});